/**
 * VideoImmersionScreen V3 — Full RLI Cinematic Nature Journey
 * 
 * Triple-buffered video engine with complete Reality Language Installation:
 * - Subliminal image flashes (1-5% opacity, ghost images from user's vision)
 * - Subliminal word flashes (organic rhythm, peripheral and center)
 * - TTS affirmation whispers (0.2 volume, subliminal)
 * - Phase-guided breathing text prompts
 * - Cinematic 3s crossfade dissolves between clips
 * - `ended` event backup prevents blank frames between clips
 * 
 * Architecture: Imperative DOM video pool, CSS transition crossfades,
 * React overlay for subliminal layers.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SkipForward, Volume2, VolumeX, Bookmark, Check } from 'lucide-react';
import { trackImmersionPlayed } from '../../lib/dailyEssentialsTracker';

const API = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_CLIPS = [
  { id: 'ocean_waves_sunrise', url: 'https://videos.pexels.com/video-files/1093662/1093662-hd_1920_1080_30fps.mp4', phase: 'safety', duration: 18 },
  { id: 'turquoise_waves_shore', url: 'https://videos.pexels.com/video-files/1409899/1409899-hd_1920_1080_25fps.mp4', phase: 'safety', duration: 16 },
  { id: 'drone_rocky_shore', url: 'https://videos.pexels.com/video-files/4153597/4153597-hd_1920_1080_25fps.mp4', phase: 'opening', duration: 16 },
  { id: 'waves_rocks_bali', url: 'https://videos.pexels.com/video-files/6981324/6981324-hd_1920_1080_25fps.mp4', phase: 'opening', duration: 16 },
  { id: 'aerial_rocky_coast', url: 'https://videos.pexels.com/video-files/7046546/7046546-hd_1920_1080_25fps.mp4', phase: 'becoming', duration: 16 },
  { id: 'ocean_waves_crashing', url: 'https://videos.pexels.com/video-files/6775422/6775422-hd_1920_1080_24fps.mp4', phase: 'becoming', duration: 14 },
  { id: 'coastal_aerial', url: 'https://videos.pexels.com/video-files/7587029/7587029-hd_1920_1080_30fps.mp4', phase: 'installation', duration: 10 },
  { id: 'waves_big_rock', url: 'https://videos.pexels.com/video-files/1828452/1828452-hd_1920_1080_24fps.mp4', phase: 'integration', duration: 10 },
];

const CROSSFADE_S = 3;

// Subliminal configuration — matches the still-image immersion system
const SUBLIMINAL = {
  baseInterval: { min: 2500, max: 5000 },
  deepInterval: { min: 1800, max: 3500 },
  flashDuration: { min: 600, max: 1800 },
  opacity: { min: 0.01, max: 0.03 },
  deepOpacity: { min: 0.02, max: 0.05 },
  fadeMs: 400,
  centerChance: 0.25,
};

const VideoImmersionScreen = ({ 
  selectedWords = [],
  selectedAffirmations = [],
  selectedImages = [],
  onComplete,
  onViewBoard,
  foundationVideoUrl = null,
}) => {
  const [clips, setClips] = useState([]);
  const [clipsLoading, setClipsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isEnding, setIsEnding] = useState(false);
  const [muted, setMuted] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  
  // RLI state
  const [flashItem, setFlashItem] = useState(null);
  const [phaseText, setPhaseText] = useState('');
  const [spokenAffirmation, setSpokenAffirmation] = useState('');

  // Save-journey state — local-only optimistic flag so the bookmark
  // icon flips to a check the moment the user taps. Server still gets
  // a clean POST.
  const [savedJourney, setSavedJourney] = useState(false);

  // DOM refs
  const containerRef = useRef(null);
  const videoPoolRef = useRef({});
  const activeIndexRef = useRef(0);
  const isTransitioningRef = useRef(false);
  const clipTimerRef = useRef(null);
  const progressTimerRef = useRef(null);
  const elapsedRef = useRef(0);
  const musicRef = useRef(null);
  const mutedRef = useRef(false);
  const clipsRef = useRef([]);
  
  // RLI refs
  const flashTimerRef = useRef(null);
  const affirmationTimerRef = useRef(null);
  const affirmationIndexRef = useRef(0);
  const voiceAudioRef = useRef(null);
  const endedHandlersRef = useRef({});

  const [firstClipReady, setFirstClipReady] = useState(false);

  // iOS detection — used to show the "phone-not-on-silent" hint on the
  // Tap-to-Begin screen. iOS's physical ringer switch silences ALL HTML5
  // audio system-wide, which historically caused a stream of "I can't
  // hear the music" reports. Detect Apple-mobile UA AND coarse pointer
  // to avoid false positives on iPadOS-as-desktop. Computed once at
  // mount; never reactive.
  const isLikelyIOS = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    try {
      const ua = navigator.userAgent || '';
      const isApple = /iPhone|iPad|iPod/i.test(ua) ||
        (ua.includes('Mac') && navigator.maxTouchPoints > 1);
      const coarse = typeof window !== 'undefined' && window.matchMedia &&
        window.matchMedia('(pointer: coarse)').matches;
      return isApple && coarse;
    } catch (_) { return false; }
  }, []);

  useEffect(() => { clipsRef.current = clips; }, [clips]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  // Track when first clip is ready to play
  useEffect(() => {
    if (clips.length === 0) return;
    const checkReady = setInterval(() => {
      const entry = videoPoolRef.current[0];
      if (entry && entry.ready) {
        setFirstClipReady(true);
        clearInterval(checkReady);
      }
    }, 300);
    return () => clearInterval(checkReady);
  }, [clips]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  // Load clips — fetch from backend, shuffle within phases for unique journeys
  useEffect(() => {
    const shuffleArray = (arr) => {
      const shuffled = [...arr];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };

    const buildJourney = (allClips) => {
      // Group by phase
      const phases = ['safety', 'opening', 'becoming', 'installation', 'integration'];
      const grouped = {};
      phases.forEach(p => { grouped[p] = []; });
      allClips.forEach(c => {
        if (grouped[c.phase]) grouped[c.phase].push(c);
        else grouped.integration.push(c); // fallback
      });

      // Shuffle within each phase, then concatenate in arc order
      const journey = [];
      phases.forEach(phase => {
        const shuffled = shuffleArray(grouped[phase]);
        journey.push(...shuffled);
      });
      return journey;
    };

    const loadClips = async () => {
      // If a foundation video is provided, probe it first — fall back gracefully
      // when the file is unplayable in this browser (e.g. .mov on Chrome) or 404.
      if (foundationVideoUrl) {
        const url = `${API}${foundationVideoUrl}`;
        try {
          const probe = await fetch(url, { method: 'GET', headers: { Range: 'bytes=0-1' } });
          if (probe.ok || probe.status === 206) {
            const ctype = probe.headers.get('content-type') || '';
            const v = document.createElement('video');
            const canPlay = ctype && v.canPlayType(ctype);
            // Foundation file is playable in this browser — use it.
            if (canPlay && canPlay !== '') {
              setClips([{ id: 'foundation', url, phase: 'full', duration: 600 }]);
              setClipsLoading(false);
              return;
            }
            console.warn(
              `[VideoImmersion] Foundation video content-type "${ctype}" not playable in this browser — falling back to clip journey.`
            );
          } else {
            console.warn(`[VideoImmersion] Foundation video probe returned ${probe.status} — falling back to clip journey.`);
          }
        } catch (err) {
          console.warn('[VideoImmersion] Foundation video probe failed — falling back to clip journey:', err);
        }
        // fall through to clip journey
      }
      try {
        const res = await fetch(`${API}/api/videos/list`);
        if (res.ok) {
          const data = await res.json();
          if (data.videos && data.videos.length > 0) {
            const allClips = data.videos.map(v => ({
              id: v.file_id,
              url: `${API}${v.stream_url}`,
              phase: v.phase,
              duration: v.duration,
            }));
            setClips(buildJourney(allClips));
            setClipsLoading(false);
            return;
          }
        }
      } catch (e) {
        console.warn('Failed to load custom videos, using fallback:', e);
      }
      setClips(buildJourney(FALLBACK_CLIPS));
      setClipsLoading(false);
    };
    loadClips();
  }, []);

  const totalDuration = useMemo(() => clips.reduce((sum, c) => sum + c.duration, 0), [clips]);

  useEffect(() => {
    const t = setTimeout(() => setShowSkip(true), 5000);
    return () => clearTimeout(t);
  }, []);

  // ========================================
  // PHASE SYSTEM — determines subliminal intensity
  // ========================================
  const getCurrentPhase = useCallback((elapsed) => {
    if (totalDuration === 0) return 'safety';
    const ratio = elapsed / totalDuration;
    if (ratio < 0.17) return 'safety';
    if (ratio < 0.38) return 'opening';
    if (ratio < 0.58) return 'becoming';
    if (ratio < 0.78) return 'installation';
    return 'integration';
  }, [totalDuration]);

  // ========================================
  // SUBLIMINAL FLASH ENGINE
  // ========================================
  const triggerFlash = useCallback(() => {
    const phase = getCurrentPhase(elapsedRef.current);
    const isDeep = phase === 'becoming' || phase === 'installation';

    const opCfg = isDeep ? SUBLIMINAL.deepOpacity : SUBLIMINAL.opacity;
    const opacity = opCfg.min + Math.random() * (opCfg.max - opCfg.min);
    const duration = SUBLIMINAL.flashDuration.min + Math.random() * (SUBLIMINAL.flashDuration.max - SUBLIMINAL.flashDuration.min);

    // Position — 25% center, 75% peripheral
    let position;
    if (Math.random() < SUBLIMINAL.centerChance) {
      position = { left: `${35 + Math.random() * 30}%`, top: `${35 + Math.random() * 30}%` };
    } else {
      const edges = [
        { left: `${5 + Math.random() * 15}%`, top: `${20 + Math.random() * 60}%` },
        { left: `${80 + Math.random() * 15}%`, top: `${20 + Math.random() * 60}%` },
        { left: `${5 + Math.random() * 25}%`, top: `${5 + Math.random() * 20}%` },
        { left: `${70 + Math.random() * 25}%`, top: `${5 + Math.random() * 20}%` },
        { left: `${5 + Math.random() * 25}%`, top: `${75 + Math.random() * 20}%` },
        { left: `${70 + Math.random() * 25}%`, top: `${75 + Math.random() * 20}%` },
      ];
      position = edges[Math.floor(Math.random() * edges.length)];
    }

    // Choose content: 60% image (if available), 40% word
    const useImage = Math.random() > 0.4 && selectedImages.length > 0;
    const content = useImage
      ? selectedImages[Math.floor(Math.random() * selectedImages.length)]
      : selectedWords[Math.floor(Math.random() * selectedWords.length)];

    if (!content) {
      // Schedule next even if no content
      const intCfg = isDeep ? SUBLIMINAL.deepInterval : SUBLIMINAL.baseInterval;
      flashTimerRef.current = setTimeout(triggerFlash, intCfg.min + Math.random() * (intCfg.max - intCfg.min));
      return;
    }

    setFlashItem({ type: useImage ? 'image' : 'word', content, opacity, position, duration });

    setTimeout(() => setFlashItem(null), duration + SUBLIMINAL.fadeMs * 2);

    // Schedule next flash
    const intCfg = isDeep ? SUBLIMINAL.deepInterval : SUBLIMINAL.baseInterval;
    flashTimerRef.current = setTimeout(triggerFlash, intCfg.min + Math.random() * (intCfg.max - intCfg.min));
  }, [selectedImages, selectedWords, getCurrentPhase]);

  // ========================================
  // TTS AFFIRMATION WHISPERS
  // ========================================
  const playAffirmationTTS = useCallback(async (text) => {
    try {
      const response = await fetch(`${API}/api/immersion/affirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.audio_url && !mutedRef.current) {
          if (voiceAudioRef.current) {
            voiceAudioRef.current.pause();
            voiceAudioRef.current = null;
          }
          voiceAudioRef.current = new Audio(data.audio_url);
          voiceAudioRef.current.volume = 0.2; // Subliminal whisper
          voiceAudioRef.current.play().catch(() => {});
        }
      }
    } catch (e) {
      // Silent fail — text overlay still works
    }
  }, []);

  const startAffirmationCycle = useCallback(() => {
    if (selectedAffirmations.length === 0) return;

    const showNext = () => {
      const idx = affirmationIndexRef.current % selectedAffirmations.length;
      const text = selectedAffirmations[idx];
      affirmationIndexRef.current++;

      setSpokenAffirmation(text);
      playAffirmationTTS(text);

      setTimeout(() => setSpokenAffirmation(''), 5000);
    };

    // First affirmation after 12s, then every 16s
    const initial = setTimeout(showNext, 12000);
    affirmationTimerRef.current = setInterval(showNext, 16000);

    return () => {
      clearTimeout(initial);
      if (affirmationTimerRef.current) clearInterval(affirmationTimerRef.current);
    };
  }, [selectedAffirmations, playAffirmationTTS]);

  // ========================================
  // PHASE TEXT — subtle prompts at key moments
  // ========================================
  useEffect(() => {
    if (!audioUnlocked) return;
    const interval = setInterval(() => {
      const phase = getCurrentPhase(elapsedRef.current);
      const texts = {
        safety: '',
        opening: 'Allow yourself to open...',
        becoming: '',
        installation: '',
        integration: 'This is now within you',
      };
      setPhaseText(texts[phase] || '');
    }, 2000);
    return () => clearInterval(interval);
  }, [audioUnlocked, getCurrentPhase]);

  // ========================================
  // BACKGROUND MUSIC — Hero · Cinematic ("Quiet trailer, lifted")
  // ========================================
  // The track was originally created inside a useEffect, then started in
  // handleTapToBegin. That pattern played badly with React 18 StrictMode
  // in dev: the cleanup function tore down the Audio object mid-fetch,
  // leaving the trailer_calm.mp3 download cancelled (`net::ERR_ABORTED`)
  // and the user hearing nothing. Solution: instantiate the Audio inside
  // the user gesture itself — single, stable, no cleanup race.
  const IMMERSION_TRACKS = ['trailer_calm.mp3', 'trailer_calm_alt.mp3'];
  const selectedTrack = useMemo(() => IMMERSION_TRACKS[Math.floor(Math.random() * IMMERSION_TRACKS.length)], []);

  // Music is created inside handleTapToBegin (below) — see comment above
  // for the StrictMode reasoning. No useEffect-level audio lifecycle.
  // We still guarantee cleanup on real unmount via this dedicated effect
  // with an empty dep array — runs once at mount, cleans the ref once at
  // actual unmount. (StrictMode also double-invokes empty-dep effects in
  // dev, but the cleanup checks `musicRef.current` before pausing so a
  // ref that hasn't been populated yet is a no-op.)
  useEffect(() => {
    return () => {
      if (musicRef.current) {
        try {
          musicRef.current.pause();
          musicRef.current.src = '';
        } catch (_) { /* noop */ }
        musicRef.current = null;
      }
    };
  }, []);

  // ========================================
  // VIDEO ENGINE
  // ========================================

  const createVideo = useCallback((index) => {
    const allClips = clipsRef.current;
    if (index < 0 || index >= allClips.length) return null;
    if (videoPoolRef.current[index]) return videoPoolRef.current[index].video;

    const clip = allClips[index];
    const video = document.createElement('video');
    video.src = clip.url;
    video.preload = 'auto';
    video.playsInline = true;
    video.muted = true;
    video.loop = !!foundationVideoUrl; // Loop foundation video for full RLI duration
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    Object.assign(video.style, {
      position: 'absolute',
      top: '0', left: '0', width: '100%', height: '100%',
      objectFit: 'cover',
      opacity: '0',
      transition: `opacity ${CROSSFADE_S}s ease-in-out`,
      zIndex: String(index),
      filter: 'brightness(0.88) saturate(0.92)',
    });

    const entry = { video, ready: false };

    const onCanPlay = () => {
      entry.ready = true;
      video.removeEventListener('canplay', onCanPlay);
    };
    const onErr = () => {
      // Video file failed to load — mark "ready" so the engine doesn't block,
      // and skip forward on the next tick.
      console.warn(`[VideoImmersion] Clip ${index} (${clip.id}) failed to load:`, video.error);
      entry.ready = true;
      entry.errored = true;
      video.removeEventListener('error', onErr);
    };
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('error', onErr);
    if (video.readyState >= 3) entry.ready = true;

    videoPoolRef.current[index] = entry;

    if (containerRef.current) {
      containerRef.current.appendChild(video);
    }

    video.load();
    return video;
  }, []);

  const destroyVideo = useCallback((index) => {
    // Remove ended handler first
    const handler = endedHandlersRef.current[index];
    const entry = videoPoolRef.current[index];
    if (entry && handler) {
      entry.video.removeEventListener('ended', handler);
      delete endedHandlersRef.current[index];
    }
    if (!entry) return;
    entry.video.pause();
    entry.video.removeAttribute('src');
    entry.video.load();
    if (entry.video.parentNode) entry.video.parentNode.removeChild(entry.video);
    delete videoPoolRef.current[index];
  }, []);

  // Core transition function
  const advanceClip = useCallback(() => {
    if (isTransitioningRef.current) return;
    const allClips = clipsRef.current;
    const currentIdx = activeIndexRef.current;
    const nextIdx = currentIdx + 1;

    // Clear the scheduled timer (in case `ended` event fired first)
    if (clipTimerRef.current) {
      clearTimeout(clipTimerRef.current);
      clipTimerRef.current = null;
    }

    if (nextIdx >= allClips.length) {
      setIsEnding(true);
      const currentEntry = videoPoolRef.current[currentIdx];
      if (currentEntry) {
        currentEntry.video.style.transition = 'opacity 4s ease-out';
        currentEntry.video.style.opacity = '0.4';
      }
      if (musicRef.current) {
        let vol = musicRef.current.volume;
        const fadeOut = setInterval(() => {
          vol -= 0.006;
          if (vol <= 0 || !musicRef.current) {
            clearInterval(fadeOut);
            if (musicRef.current) musicRef.current.volume = 0;
          } else {
            musicRef.current.volume = vol;
          }
        }, 50);
      }
      setTimeout(() => onComplete(), 4500);
      return;
    }

    isTransitioningRef.current = true;

    const doFade = () => {
      const nextEntry = videoPoolRef.current[nextIdx];
      if (!nextEntry) { isTransitioningRef.current = false; return; }

      // Force-mute the clip's own audio track — see comment in
      // handleTapToBegin. The music + TTS are the soundscape.
      nextEntry.video.muted = true;
      nextEntry.video.currentTime = 0;
      nextEntry.video.play().catch(() => {});

      requestAnimationFrame(() => {
        nextEntry.video.style.opacity = '1';
      });

      setTimeout(() => {
        activeIndexRef.current = nextIdx;

        const prevEntry = videoPoolRef.current[currentIdx];
        if (prevEntry) prevEntry.video.pause();

        if (currentIdx - 1 >= 0) destroyVideo(currentIdx - 1);

        // Preload the NEXT clip only (sequential, don't compete for bandwidth)
        createVideo(nextIdx + 1);

        // Attach `ended` backup on the new active video
        attachEndedHandler(nextIdx);

        isTransitioningRef.current = false;
        scheduleNext(nextIdx);
      }, CROSSFADE_S * 1000 + 300);
    };

    const nextEntry = videoPoolRef.current[nextIdx];
    if (nextEntry && nextEntry.ready) {
      doFade();
    } else if (nextEntry) {
      let resolved = false;
      const onReady = () => {
        if (resolved) return;
        resolved = true;
        nextEntry.video.removeEventListener('canplay', onReady);
        doFade();
      };
      nextEntry.video.addEventListener('canplay', onReady);
      setTimeout(() => {
        if (!resolved) { resolved = true; nextEntry.video.removeEventListener('canplay', onReady); doFade(); }
      }, 10000);
    } else {
      createVideo(nextIdx);
      setTimeout(doFade, 2000);
    }
  }, [onComplete, createVideo, destroyVideo]);

  // Attach `ended` event as a BACKUP transition trigger
  const attachEndedHandler = useCallback((index) => {
    const entry = videoPoolRef.current[index];
    if (!entry) return;

    // Remove old handler if exists
    if (endedHandlersRef.current[index]) {
      entry.video.removeEventListener('ended', endedHandlersRef.current[index]);
    }

    const handler = () => {
      // Video naturally ended — trigger transition immediately
      if (activeIndexRef.current === index && !isTransitioningRef.current) {
        advanceClip();
      }
    };
    entry.video.addEventListener('ended', handler);
    endedHandlersRef.current[index] = handler;
  }, [advanceClip]);

  const scheduleNext = useCallback((index) => {
    if (clipTimerRef.current) clearTimeout(clipTimerRef.current);
    const allClips = clipsRef.current;
    const clip = allClips[index];
    if (!clip) return;

    // Start crossfade well before clip ends — overlap the scenes
    const transitionAt = Math.max(2000, (clip.duration * 1000) - (CROSSFADE_S * 1000));

    clipTimerRef.current = setTimeout(() => {
      advanceClip();
    }, transitionAt);
  }, [advanceClip]);

  // Progress tracker
  useEffect(() => {
    if (!audioUnlocked) return;
    progressTimerRef.current = setInterval(() => {
      elapsedRef.current += 0.5;
      setProgress(Math.min(100, (elapsedRef.current / totalDuration) * 100));
    }, 500);
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [audioUnlocked, totalDuration]);

  // Preload first clip only — don't compete for bandwidth with multiple downloads
  useEffect(() => {
    if (clips.length === 0) return;
    createVideo(0);
  }, [clips, createVideo]);

  // Start journey — create + play music on gesture (Safari rule).
  // Audio is intentionally created HERE (not in a useEffect) so that
  // StrictMode dev double-invoke can't tear down the audio mid-fetch.
  const handleTapToBegin = useCallback(() => {
    setAudioUnlocked(true);

    // Phase 2: Create and play the music inside the gesture stack so
    // Safari accepts the autoplay. Volume starts at 0.55 so it's clearly
    // audible immediately; we previously used 0.30 but the user reported
    // they couldn't hear the music at all — at 0.30 with the foundation
    // video's own audio track playing over the top, the music gets
    // masked. Bumped + retry-on-stall added.
    if (!musicRef.current) {
      const audio = new Audio(`${API}/api/audio/ambient/${selectedTrack}`);
      audio.loop = true;
      audio.volume = 0.55;
      audio.preload = 'auto';
      musicRef.current = audio;
    }
    const tryPlay = () => {
      if (!musicRef.current) return;
      musicRef.current.play().catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[VideoImmersion] Music play rejected:', err);
      });
    };
    tryPlay();
    // Auto-tick Daily Essentials · Daily Immersion (session-scoped dedupe).
    trackImmersionPlayed();
    // Safety net: if the audio is still paused after 2.5s (browser threw
    // an autoplay warning, network stall, etc.), retry once more. Without
    // this the user can be stuck in silence with no idea why.
    setTimeout(() => {
      if (musicRef.current && musicRef.current.paused) {
        // eslint-disable-next-line no-console
        console.log('[VideoImmersion] Music not playing after 2.5s — retrying.');
        tryPlay();
      }
    }, 2500);

    // 2. Start first video — also in the gesture stack
    const firstEntry = videoPoolRef.current[0];
    if (firstEntry) {
      // Foundation videos keep their own audio track muted — the Hero
      // music + TTS affirmations + Aurum voice underlay are the
      // intended soundscape. Clip-journey videos also stay muted (their
      // raw audio is just stock-clip ambience that would clash with the
      // music). The user's mute toggle still works via mutedRef.
      firstEntry.video.muted = true;

      const startPlayback = () => {
        firstEntry.video.play().then(() => {}).catch(() => {});
        requestAnimationFrame(() => {
          firstEntry.video.style.opacity = '1';
        });
        if (!foundationVideoUrl) {
          attachEndedHandler(0);
          scheduleNext(0);
          // Preload next clip AFTER first starts playing (don't compete for bandwidth)
          createVideo(1);
        }
      };

      if (firstEntry.ready) {
        startPlayback();
      } else {
        // Wait for first clip to be ready
        const onReady = () => {
          firstEntry.video.removeEventListener('canplay', onReady);
          startPlayback();
        };
        firstEntry.video.addEventListener('canplay', onReady);
      }
    }

    // 3. Start subliminal flashes after 3s
    flashTimerRef.current = setTimeout(triggerFlash, 3000);

    // 4. Start affirmation cycle
    startAffirmationCycle();
  }, [scheduleNext, triggerFlash, startAffirmationCycle, attachEndedHandler, createVideo]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (clipTimerRef.current) clearTimeout(clipTimerRef.current);
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      if (affirmationTimerRef.current) clearInterval(affirmationTimerRef.current);
      if (musicRef.current) { musicRef.current.pause(); musicRef.current = null; }
      if (voiceAudioRef.current) { voiceAudioRef.current.pause(); voiceAudioRef.current = null; }
      Object.keys(videoPoolRef.current).forEach(k => {
        const entry = videoPoolRef.current[k];
        if (entry) { entry.video.pause(); entry.video.removeAttribute('src'); entry.video.load(); }
      });
      videoPoolRef.current = {};
      endedHandlersRef.current = {};
    };
  }, []);

  const handleSkip = () => {
    if (clipTimerRef.current) clearTimeout(clipTimerRef.current);
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    if (affirmationTimerRef.current) clearInterval(affirmationTimerRef.current);
    if (musicRef.current) { musicRef.current.pause(); musicRef.current = null; }
    if (voiceAudioRef.current) { voiceAudioRef.current.pause(); voiceAudioRef.current = null; }
    onComplete();
  };

  const toggleMute = () => {
    setMuted(m => {
      const newMuted = !m;
      if (musicRef.current) musicRef.current.muted = newMuted;
      if (voiceAudioRef.current) voiceAudioRef.current.muted = newMuted;
      const entry = videoPoolRef.current[activeIndexRef.current];
      if (entry) entry.video.muted = newMuted;
      return newMuted;
    });
  };

  // Save the exact journey configuration to My Collection as a
  // recallable session recipe. Fire-and-forget POST; we flip the icon
  // optimistically and revert on hard failure so the immersion never
  // breaks for a save error. Auth-required — silently no-op if no token.
  const handleSaveJourney = useCallback(async () => {
    if (savedJourney) return; // single-shot — don't dup-save on multi-tap
    setSavedJourney(true);
    try {
      const token = localStorage.getItem('reclaim_token');
      if (!token) return;
      await fetch(`${API}/api/library/session-recipe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: 'Saved journey',
          music_track: selectedTrack,
          music_label: 'Hero · Cinematic',
          foundation_video_url: foundationVideoUrl,
          affirmations: (selectedAffirmations || []).slice(0, 24),
          image_urls: (selectedImages || []).map((i) => i?.url).filter(Boolean).slice(0, 16),
          duration_seconds: Math.round(totalDuration),
          source_page: foundationVideoUrl && foundationVideoUrl.includes('/aurum') ? 'aurum_vision' : 'living_vision_board',
        }),
      });
    } catch (_) {
      // Revert on hard network failure so the user can retry.
      setSavedJourney(false);
    }
  }, [savedJourney, selectedTrack, foundationVideoUrl, selectedAffirmations, selectedImages, totalDuration]);

  const remainingSeconds = Math.max(0, Math.ceil(totalDuration - elapsedRef.current));
  const remainingMinutes = Math.ceil(remainingSeconds / 60);

  if (clipsLoading || clips.length === 0) {
    return (
      <motion.div
        className="fixed inset-0 bg-black flex items-center justify-center"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        data-testid="video-immersion-loading"
      >
        <motion.p
          className="text-white/30 text-lg"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          animate={{ opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          Preparing your immersion...
        </motion.p>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="fixed inset-0 bg-black overflow-hidden select-none"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="video-immersion-screen"
    >
      {/* Video container */}
      <div ref={containerRef} className="absolute inset-0" />

      {/* Cinematic vignette */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex: 50, background: 'radial-gradient(ellipse 85% 85% at 50% 50%, transparent 45%, rgba(0,0,0,0.28) 100%)' }}
      />

      {/* Warm cinematic grade */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex: 51, background: 'linear-gradient(180deg, rgba(180,150,100,0.03) 0%, transparent 30%, transparent 75%, rgba(0,0,0,0.12) 100%)' }}
      />

      {/* =============================================
          SUBLIMINAL FLASH LAYER — ultra-subtle, organic
          ============================================= */}
      <AnimatePresence>
        {flashItem && (
          <motion.div
            className="absolute pointer-events-none"
            style={{
              left: flashItem.position.left,
              top: flashItem.position.top,
              transform: 'translate(-50%, -50%)',
              zIndex: 55,
            }}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: flashItem.opacity, scale: 1 }}
            exit={{ opacity: 0, scale: 1.01 }}
            transition={{ duration: SUBLIMINAL.fadeMs / 1000, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {flashItem.type === 'image' ? (
              <img 
                src={flashItem.content.url || flashItem.content} 
                alt="" 
                className="w-24 md:w-36 h-auto object-cover rounded-2xl"
                style={{ filter: 'blur(2px) saturate(0.7)', mixBlendMode: 'screen' }}
              />
            ) : (
              <span 
                className="text-2xl md:text-4xl text-white/80 font-light"
                style={{ 
                  fontFamily: "'Playfair Display', serif", fontStyle: 'italic',
                  textShadow: '0 0 40px rgba(255,255,255,0.15)', mixBlendMode: 'soft-light',
                }}
              >
                {flashItem.content}
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tap to begin */}
      {!audioUnlocked && (
        <motion.div
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          style={{ zIndex: 100, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(12px)' }}
          onClick={firstClipReady ? handleTapToBegin : undefined}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          data-testid="video-tap-to-begin"
        >
          <motion.div className="text-center">
            <motion.p
              className="text-white/30 text-xs mb-6 tracking-[0.25em] uppercase"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            >
              Video Immersion
            </motion.p>
            {firstClipReady ? (
              <motion.p
                className="text-white/55 text-xl"
                style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', letterSpacing: '0.08em' }}
                animate={{ opacity: [0.25, 0.65, 0.25] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
              >
                Tap to begin
              </motion.p>
            ) : (
              <motion.p
                className="text-white/30 text-base"
                style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
                animate={{ opacity: [0.15, 0.35, 0.15] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              >
                Preparing your journey...
              </motion.p>
            )}
            <motion.p
              className="text-white/15 text-xs mt-5"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
            >
              {Math.ceil(totalDuration / 60)} min journey
            </motion.p>
            {/* Track-credit subtitle — quiet detail à la Apple Music's
                pre-play track label, so the user knows what's about to
                play. Matches the music actually chosen for the session. */}
            <motion.p
              className="text-white/25 text-[10px] mt-2 tracking-[0.32em] uppercase"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
            >
              <span style={{ marginRight: '0.5em' }}>♫</span>Hero · Cinematic
            </motion.p>
            {/* iOS silent-switch reminder — the physical ringer/silent
                switch on iPhone silences ALL HTML5 audio system-wide. We
                detect iOS by UA + coarse-pointer; show only there to
                avoid noise on desktop. Style: low-opacity gold whisper
                so it doesn't break the calm aesthetic. */}
            {isLikelyIOS && (
              <motion.p
                className="text-amber-200/40 text-[10px] mt-4 tracking-[0.18em] leading-snug"
                style={{ fontFamily: "'Manrope', sans-serif", maxWidth: '18rem', margin: '1rem auto 0' }}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}
                data-testid="ios-silent-mode-hint"
              >
                Tip — make sure your phone isn&rsquo;t on silent for sound
              </motion.p>
            )}
          </motion.div>
        </motion.div>
      )}

      {/* Phase text — subtle prompts at key emotional moments */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 58 }}>
        <AnimatePresence mode="wait">
          {phaseText && !spokenAffirmation && (
            <motion.p
              key={phaseText}
              className="text-xl md:text-2xl text-white/25 text-center px-8 max-w-lg"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 2 }}
            >
              {phaseText}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Spoken affirmation display — very faint, synced with TTS whisper */}
      <AnimatePresence>
        {spokenAffirmation && (
          <motion.div
            className="absolute bottom-32 left-0 right-0 flex justify-center pointer-events-none"
            style={{ zIndex: 59 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 0.22, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 2, ease: 'easeOut' }}
          >
            <p 
              className="text-base md:text-lg text-white text-center px-12 max-w-2xl"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}
            >
              {spokenAffirmation}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ending */}
      <AnimatePresence>
        {isEnding && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            style={{ zIndex: 70 }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 3 }}
          >
            <motion.div 
              className="absolute inset-0"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 4 }}
              style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.5) 100%)' }}
            />
            <motion.p
              className="relative text-xl text-white/45"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', letterSpacing: '0.06em' }}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 2 }}
            >
              This is now within you
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Controls */}
      {audioUnlocked && !isEnding && (
        <div className="absolute top-6 right-6 flex items-center gap-3" style={{ zIndex: 80 }}>
          <motion.button
            onClick={toggleMute}
            className="p-2.5 rounded-full"
            style={{ background: 'rgba(0,0,0,0.22)', backdropFilter: 'blur(8px)' }}
            whileTap={{ scale: 0.9 }}
            data-testid="video-mute-btn"
          >
            {muted ? <VolumeX size={15} className="text-white/30" /> : <Volume2 size={15} className="text-white/30" />}
          </motion.button>
        </div>
      )}

      {/* Save journey — bookmark the exact music + visuals + affirmations
          combo into My Collection. Tap-once design (no long-press needed):
          discoverable, no accidental trigger risk. Sits opposite the Skip
          control so the user has a clear "save vs leave" choice. */}
      {audioUnlocked && !isEnding && (
        <motion.button
          className="absolute top-6 flex items-center gap-2 px-3.5 py-2 rounded-full text-xs text-white/35 hover:text-white/65"
          style={{
            right: '88px', // sits to the left of the Skip button
            zIndex: 80,
            background: 'rgba(0,0,0,0.18)',
            backdropFilter: 'blur(8px)',
            fontFamily: "'Manrope', sans-serif",
          }}
          onClick={handleSaveJourney}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 5 }}
          whileTap={{ scale: 0.94 }}
          data-testid="video-save-journey-btn"
          aria-label={savedJourney ? 'Journey saved' : 'Save this journey to My Collection'}
        >
          {savedJourney
            ? <><Check size={12} style={{ color: 'rgba(217, 180, 120, 0.95)' }} /><span style={{ color: 'rgba(217, 180, 120, 0.85)' }}>Saved · Replay anytime →</span></>
            : <><Bookmark size={12} /> Save journey</>}
        </motion.button>
      )}

      {/* Skip */}
      <AnimatePresence>
        {showSkip && audioUnlocked && !isEnding && (
          <motion.button
            className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 rounded-full text-xs text-white/20"
            style={{ zIndex: 80, background: 'rgba(0,0,0,0.18)', backdropFilter: 'blur(8px)', fontFamily: "'Manrope', sans-serif" }}
            onClick={handleSkip}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            whileTap={{ scale: 0.95 }}
            data-testid="video-skip-btn"
          >
            <SkipForward size={12} /> Skip
          </motion.button>
        )}
      </AnimatePresence>

      {/* View Board */}
      {audioUnlocked && !isEnding && onViewBoard && (
        <motion.button
          className="absolute bottom-16 right-6 px-4 py-2 rounded-full text-xs text-white/18"
          style={{ zIndex: 80, background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(8px)', fontFamily: "'Manrope', sans-serif" }}
          onClick={onViewBoard}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3 }}
          whileTap={{ scale: 0.95 }}
          data-testid="video-view-board-btn"
        >
          View my board
        </motion.button>
      )}

      {/* Progress */}
      {audioUnlocked && !isEnding && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-48" style={{ zIndex: 80 }}>
          <div className="h-px bg-white/6 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ background: 'rgba(255,255,255,0.14)', width: `${progress}%`, transition: 'width 1s linear' }}
            />
          </div>
          <p className="text-center text-white/10 text-[10px] mt-3 tracking-wider" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {remainingMinutes > 0 ? `${remainingMinutes} min` : ''}
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default VideoImmersionScreen;
